import { When } from 'react-if';

import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import { CONTAINER_TAG_TYPE } from '@/components/@base/container/type';
import List, { ListItem } from '@/components/@base/list';
import { Heading, Text } from '@/components/@base/typography';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import XImage from '@/components/@base/x-image';
import Section from '@/components/app/product/components/@shared/section';
import Props from '@/components/app/product/components/content/change-log/type';
import { Updater } from '@/components/app/product/components/content/updater';
import RoundedIcon from '@/components/shared/components/icon/rounded';
import IconTextCombo from '@/components/shared/components/icon-text-combo';
import { formatDate } from '@/components/shared/helpers';

import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function ChangeLog({ totalVersions, log, showSmartUpdater }: Props) {
  const isSmallScreen = useClientSmallScreen();
  return (
    <Section
      id='productChangelogSection'
      className='mx-0!'
      expandable
      showMoreLabel={!isSmallScreen ? productLocalization.seeAllChanges : undefined}
    >
      <Container center className='mb-2 justify-between md:mb-4'>
        <IconTextCombo
          id='productChangelog'
          containerId='productChangelogTitle'
          Icon={
            <RoundedIcon
              icon={
                <XImage
                  src={imagesList.icons.changelogIcon}
                  alt={productLocalization.changelog}
                  width={16}
                  height={16}
                />
              }
            />
          }
          headingType={HEADING_TYPE.H2}
          title={productLocalization.changelogTitle}
          textClassName='text-base font-bold text-[#424B57]'
          wrapperClassName='max-w-max'
        />
        <Container
          className='rounded-md bg-[#F0F8FF] px-4 py-2 text-[13px] font-bold text-[#8A9CB7]'
          type={CONTAINER_TAG_TYPE.SPAN}
        >
          {totalVersions} {productLocalization.updates}
        </Container>
      </Container>

      <When condition={showSmartUpdater}>
        <Updater />
      </When>
      <Container>
        {log?.map((log, index) => (
          <List
            key={index}
            className='bg-[#F5F9FF] px-[5px] py-1 first:rounded-t-md last:rounded-b-md md:first:rounded-t-lg md:last:rounded-b-lg [&>*:nth-child(even)]:bg-[#EFF5FF]'
          >
            <ListItem className='my-2 rounded-md px-2.5 py-2 md:px-[18px]'>
              <Container center className='justify-between'>
                <Container center className='gap-2'>
                  <Heading type={HEADING_TYPE.H3} className='text-sm font-bold text-[#5B5C60]'>
                    {productLocalization.versionChanges}
                  </Heading>
                  <Container
                    center
                    className='max-h-[22px] rounded-md bg-[#6097F3] px-2 py-1 text-sm font-bold text-[#FFFFFF]'
                  >
                    {log.version}
                  </Container>
                </Container>
                <Text className='text-[#95A4BA]'>{formatDate(log.releaseDate)}</Text>
              </Container>
            </ListItem>
            {log.features?.map((feature, index) => (
              <ListItem key={index} className='my-1 rounded-md p-2 text-[#7788A1] last:mb-0'>
                <Text>{feature}</Text>
              </ListItem>
            ))}
          </List>
        ))}
      </Container>
    </Section>
  );
}
